import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Tooltip,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Button,
  Select,
  MenuItem,
  Input,
  ToggleButtonGroup,
  ToggleButton,
  InputLabel,
  FormControl,
  TextField,
  Paper,
  Slider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  Chip,
  Avatar
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { 
  FormatBold, 
  FormatItalic, 
  RestartAlt, 
  FormatAlignLeft, 
  FormatAlignCenter, 
  FormatAlignRight,
  CloudUpload,
  Delete,
  Image as ImageIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import type { CustomModule, ModuleSettings, TitleStyles } from '../ECommercePage';
import SimpleProductSelectionModal from './SimpleProductSelectionModal';
import CategorySelectionModal from './CategorySelectionModal';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import { getProductsByIds, getEcommerceProducts, getPublicCategoriesWithHierarchy } from '../../../services/ecommerceApi';
import { applyHomepageModuleTemplate } from './homepageModuleTemplates';
import BrandFlowModuleEditor from './brandFlow/BrandFlowModuleEditor';
import { useTranslation } from 'react-i18next';
import HTMLEditor from './HTMLEditor';




// CategorySettings'den kopyalanan yardımcı bileşenler
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
  }
}));

// StyleButtonGroup bileşeni ve ilgili importlar (GridView, ViewList, ViewCarousel, DisplayStyle) kaldırıldı.


interface ModuleSekmesiProps {
  module: CustomModule;
  onDelete: () => void;
  onModuleUpdate: (module: CustomModule) => void;
  onEditModule?: () => void; // Modül adını düzenle
  companyId: string;
}

type ModuleImageSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16;
const MODULE_IMAGE_SLOTS: ModuleImageSlot[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const MODULE_IMAGE_URL_KEYS = [
  'imageUrl',
  'imageUrl2',
  'imageUrl3',
  'imageUrl4',
  'imageUrl5',
  'imageUrl6',
  'imageUrl7',
  'imageUrl8',
  'imageUrl9',
  'imageUrl10',
  'imageUrl11',
  'imageUrl12',
  'imageUrl13',
  'imageUrl14',
  'imageUrl15',
  'imageUrl16',
] as const;
const MAX_MODULE_IMAGE_SLOTS = MODULE_IMAGE_SLOTS.length;

const getHighestFilledImageSlot = (moduleImage: ModuleSettings['moduleImage'] | undefined): number => {
  if (!moduleImage) return 3;
  let highest = 0;
  MODULE_IMAGE_URL_KEYS.forEach((key, idx) => {
    const value = (moduleImage as any)?.[key];
    if (typeof value === 'string' && value.trim() !== '') {
      highest = idx + 1;
    }
  });
  return Math.max(3, highest);
};

const ModuleSekmesi: React.FC<ModuleSekmesiProps> = ({ module, onDelete, onModuleUpdate, onEditModule, companyId }) => {
  const { t, i18n } = useTranslation('salesSystem');
  const isTurkish = i18n.language?.toLowerCase().startsWith('tr');
  const tr = (trText: string, enText: string) => (isTurkish ? trText : enText);
  const { sectionTitle, settings, order } = module;
  
  // Eski modüllerin maxProducts değerini otomatik güncelle
  useEffect(() => {
    if (settings.maxProducts === 8) {
      console.log('🔄 Eski modül tespit edildi, maxProducts 8 -> 12 güncelleniyor:', sectionTitle);
      handleSettingChange('maxProducts', 12);
    }
  }, [settings.maxProducts, sectionTitle]);

  // Eski modüllerin resim boyutlarını otomatik güncelle
  useEffect(() => {
    if (settings.moduleImage?.width === 300 && settings.moduleImage?.height === 200) {
      console.log('🔄 Eski modül resim boyutu tespit edildi, 300x200 -> 600x400 güncelleniyor:', sectionTitle);
      handleImageSettingChange('width', 600);
      handleImageSettingChange('height', 400);
    }
  }, [settings.moduleImage?.width, settings.moduleImage?.height, sectionTitle]);
  
  // Resim Kütüphanesi Modal State'leri
  const [imageLibraryModalOpen, setImageLibraryModalOpen] = useState(false);
  const [currentImageSlot, setCurrentImageSlot] = useState<ModuleImageSlot | null>(null);
  const [visibleImageSlotCount, setVisibleImageSlotCount] = useState<number>(() => getHighestFilledImageSlot(settings.moduleImage));

  // Ürün seçim state'leri
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedCategoryRows, setSelectedCategoryRows] = useState<Array<{
    id: string;
    name: string;
    imageUrl?: string;
    products: Array<{ id: string; name: string; stockCode?: string }>;
    productCount: number;
  }>>([]);

  // Slider sürüklenirken parent'a yazmayıp yerel değeri gösteriyoruz (performans).
  const [topSpacingDraft, setTopSpacingDraft] = useState<number>(settings.topSpacing ?? 0);

  useEffect(() => {
    setTopSpacingDraft(settings.topSpacing ?? 0);
  }, [settings.topSpacing]);

  // Uyarı modalı state'leri
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [pendingAlignment, setPendingAlignment] = useState<'left' | 'center' | 'right' | null>(null);
  const [warningMessage, setWarningMessage] = useState('');

  // Seçilen ürünleri yükle (SQL'den)
  useEffect(() => {
    const loadSelectedProducts = async () => {
      console.log('🔍 [MODULE] loadSelectedProducts çalışıyor:', {
        selectedProductIds: settings.selectedProductIds,
        companyId,
        length: settings.selectedProductIds?.length
      });

      if (settings.selectedProductIds && settings.selectedProductIds.length > 0 && companyId) {
        try {
          // SQL'den ürün detaylarını çek
          const products = await getProductsByIds(companyId, settings.selectedProductIds);
          
          // API'den gelen formatı modal formatına çevir
          const formattedProducts = products.map((product: any) => ({
            id: product.id,
            name: product.name || '',
            stockCode: product.stockCode || '',
            imageUrl: product.imageUrl || null,
          }));
          
          console.log('✅ [MODULE] Loaded products:', formattedProducts);
          setSelectedProducts(formattedProducts);
        } catch (error) {
          console.error('❌ [MODULE] Seçilen ürünler yüklenirken hata:', error);
          setSelectedProducts([]);
        }
      } else {
        console.log('🚫 [MODULE] No selected products or companyId');
        setSelectedProducts([]);
      }
    };

    loadSelectedProducts();
  }, [settings.selectedProductIds, companyId]);

  useEffect(() => {
    const loadSelectedCategories = async () => {
      const categoryIds = settings.selectedCategoryIds || [];
      if (!companyId || categoryIds.length === 0) {
        setSelectedCategoryRows([]);
        return;
      }

      try {
        const [hierarchy, ecommerceProducts] = await Promise.all([
          getPublicCategoriesWithHierarchy(companyId),
          getEcommerceProducts(companyId),
        ]);

        const flat: Array<{ id: string; name: string; imageUrl?: string }> = [];
        const flatten = (cats: any[]) => {
          (Array.isArray(cats) ? cats : []).forEach((cat) => {
            if (cat?.id) {
              flat.push({
                id: String(cat.id),
                name: String(cat.name || ''),
                imageUrl: cat.imageUrl || undefined,
              });
            }
            if (Array.isArray(cat?.subCategories)) flatten(cat.subCategories);
            if (Array.isArray(cat?.children)) flatten(cat.children);
          });
        };
        flatten(Array.isArray(hierarchy) ? hierarchy : []);

        const productsByCategory = (Array.isArray(ecommerceProducts) ? ecommerceProducts : []).reduce(
          (acc: Record<string, Array<{ id: string; name: string; stockCode?: string }>>, product: any) => {
            const categoryId = String(product?.categoryId || '').trim();
            if (!categoryId) return acc;
            if (!acc[categoryId]) acc[categoryId] = [];
            acc[categoryId].push({
              id: String(product?.id || ''),
              name: String(product?.name || ''),
              stockCode: product?.stockCode,
            });
            return acc;
          },
          {}
        );

        setSelectedCategoryRows(
          categoryIds.map((id) => {
            const category = flat.find((item) => item.id === id);
            const products = productsByCategory[id] || [];
            return {
              id,
              name: category?.name || id,
              imageUrl: category?.imageUrl,
              products: products.slice(0, 3),
              productCount: products.length,
            };
          })
        );
      } catch (error) {
        console.error('❌ [MODULE] Seçilen kategoriler yüklenirken hata:', error);
        setSelectedCategoryRows([]);
      }
    };

    loadSelectedCategories();
  }, [settings.selectedCategoryIds, companyId]);

  useEffect(() => {
    const highestUsedSlot = getHighestFilledImageSlot(settings.moduleImage);
    setVisibleImageSlotCount((prev) => Math.max(prev, highestUsedSlot));
  }, [settings.moduleImage]);

  const handleModuleChange = (key: keyof CustomModule, value: any) => {
    const updatedModule = { ...module, [key]: value };
    onModuleUpdate(updatedModule);
  };

  const handleSettingChange = (settingKey: keyof ModuleSettings, value: any) => {
    const newSettings = { ...settings, [settingKey]: value };
    handleModuleChange('settings', newSettings);
  };

  const handleSettingsPatch = (patch: Partial<ModuleSettings>) => {
    handleModuleChange('settings', { ...settings, ...patch });
  };

  const isCategorySource = settings.productSource === 'category';
  const isBrandFlow = settings.moduleTemplate === 'brand-flow';

  const handleCategorySelection = (categories: Array<{ id: string; name: string }>) => {
    handleSettingsPatch({
      productSource: 'category',
      selectedCategoryIds: categories.map((category) => category.id),
      showViewAllButton: settings.showViewAllButton !== false,
      viewAllButtonText: settings.viewAllButtonText || 'Tümü',
      categoryProductLimit: settings.categoryProductLimit || 3,
    });
    setCategoryModalOpen(false);
  };

  const handleRemoveCategory = (categoryId: string) => {
    handleSettingChange(
      'selectedCategoryIds',
      (settings.selectedCategoryIds || []).filter((id) => id !== categoryId)
    );
  };

  const handleTitleStyleChange = (property: keyof TitleStyles, value: any) => {
    handleSettingChange('titleStyles', {
      ...settings.titleStyles,
      [property]: value,
    });
  };

  const resetTitleStyles = () => {
    handleSettingChange('titleStyles', {
      fontSize: 24,
      color: '#111827',
      bold: true,
      italic: false,
      fontFamily: 'Inter',
      textAlign: 'center',
    });
  };

  const fontOptions = [
    // Varsayılan
    'Inter',
    
    // ÇOK FARKLI VE MODERN FONTLAR - TEST İÇİN
    'Orbitron', // Futuristik
    'Bangers', // Çizgi roman tarzı
    'Creepster', // Korku filmi tarzı
    'Righteous', // Güçlü ve belirgin
    'Fredoka One', // Eğlenceli ve yuvarlak
    'Bungee', // Renkli ve dikkat çekici
    'Permanent Marker', // El yazısı marker
    'Russo One', // Güçlü ve modern
    'Audiowide', // Teknolojik
    'Black Ops One', // Askeri tarz
    'Orbitron', // Uzay teması
    'Exo 2', // Modern ve temiz
    
    // KLASIK MODERN FONTLAR
    'Roboto', 
    'Open Sans', 
    'Lato', 
    'Montserrat', 
    'Oswald', 
    'Raleway', 
    'Poppins', 
    'Nunito', 
    'Merriweather',
    'Work Sans',
    'Source Sans Pro',
    'Ubuntu',
    
    // STANDART FONTLAR
    'Arial',
    'Helvetica',
    'Georgia',
    'Times New Roman',
    'Verdana'
  ];

  // Resim Kütüphanesi Fonksiyonları
  const handleOpenImageLibrary = (imageNumber: ModuleImageSlot) => {
    setCurrentImageSlot(imageNumber);
    setImageLibraryModalOpen(true);
  };

  const handleSaveImageFromLibrary = (selectedImageUrls: string[]) => {
    if (!currentImageSlot || selectedImageUrls.length === 0) return;

    // Mevcut modül resim ayarlarını al veya varsayılan değerler kullan
    const currentModuleImage = settings.moduleImage || {
      imageUrl: '',
      imageUrl2: '',
      imageUrl3: '',
      imageUrl4: '',
      imageUrl5: '',
      imageUrl6: '',
      imageUrl7: '',
      imageUrl8: '',
      imageUrl9: '',
      imageUrl10: '',
      imageUrl11: '',
      imageUrl12: '',
      imageUrl13: '',
      imageUrl14: '',
      imageUrl15: '',
      imageUrl16: '',
      width: 600,
      height: 400,
      alignment: 'center' as const,
      productLayout: {
        rows: 2,
        columns: 2
      }
    };

    // 3., 4., 5. ve 6. resim alanlarının var olduğundan emin ol
    if (!currentModuleImage.imageUrl3) currentModuleImage.imageUrl3 = '';
    if (!currentModuleImage.imageUrl4) currentModuleImage.imageUrl4 = '';
    if (!currentModuleImage.imageUrl5) currentModuleImage.imageUrl5 = '';
    if (!currentModuleImage.imageUrl6) currentModuleImage.imageUrl6 = '';
    if (!currentModuleImage.imageUrl7) currentModuleImage.imageUrl7 = '';
    if (!currentModuleImage.imageUrl8) currentModuleImage.imageUrl8 = '';
    if (!(currentModuleImage as any).imageUrl9) (currentModuleImage as any).imageUrl9 = '';
    if (!(currentModuleImage as any).imageUrl10) (currentModuleImage as any).imageUrl10 = '';
    if (!(currentModuleImage as any).imageUrl11) (currentModuleImage as any).imageUrl11 = '';
    if (!(currentModuleImage as any).imageUrl12) (currentModuleImage as any).imageUrl12 = '';
    if (!(currentModuleImage as any).imageUrl13) (currentModuleImage as any).imageUrl13 = '';
    if (!(currentModuleImage as any).imageUrl14) (currentModuleImage as any).imageUrl14 = '';
    if (!(currentModuleImage as any).imageUrl15) (currentModuleImage as any).imageUrl15 = '';
    if (!(currentModuleImage as any).imageUrl16) (currentModuleImage as any).imageUrl16 = '';

    // Çoklu seçimde görselleri mevcut slottan başlayarak sırayla yerleştir
    const startIndex = currentImageSlot - 1;
    const maxFillCount = MODULE_IMAGE_URL_KEYS.length - startIndex;
    const urlsToApply = selectedImageUrls.slice(0, maxFillCount);

    const newModuleImage = {
      ...currentModuleImage,
      // İlk resim yükleniyorsa ve boyutlar henüz ayarlanmamışsa varsayılan değerleri kullan
      width: currentModuleImage.width || 600,
      height: currentModuleImage.height || 400,
      alignment: currentModuleImage.alignment || 'center' as const,
      productLayout: currentModuleImage.productLayout || {
        rows: 2,
        columns: 2
      }
    };
    urlsToApply.forEach((url, idx) => {
      const key = MODULE_IMAGE_URL_KEYS[startIndex + idx];
      (newModuleImage as any)[key] = url;
    });

    handleSettingChange('moduleImage', newModuleImage);

    // Modal'ı kapat
    setImageLibraryModalOpen(false);
    setCurrentImageSlot(null);

    console.log(`✅ Resimler ${currentImageSlot}. slottan başlayarak kaydedildi:`, urlsToApply.length);
  };

  const handleRemoveImage = (imageNumber: ModuleImageSlot) => {
    const currentModuleImage = settings.moduleImage;
    if (!currentModuleImage) return;

    // Resmi sil
    const imageUrlKey = MODULE_IMAGE_URL_KEYS[imageNumber - 1];
    
    const newModuleImage = {
      ...currentModuleImage,
      [imageUrlKey]: ''
    };
    
    // Eğer hiç resim kalmadıysa tüm moduleImage'ı sil
    const hasAnyImage = MODULE_IMAGE_URL_KEYS.some((key) => Boolean((newModuleImage as any)[key]));
    
    if (!hasAnyImage) {
      handleSettingChange('moduleImage', undefined);
    } else {
      handleSettingChange('moduleImage', newModuleImage);
    }

    console.log(`🗑️ Resim ${imageNumber} silindi`);
  };

  const handleImageSettingChange = (property: keyof NonNullable<ModuleSettings['moduleImage']>, value: any) => {
    const displayOnlyProps = new Set([
      'moduleDisplayMode',
      'pageSidePadding',
      'moduleSliderAutoplay',
      'moduleSliderInterval',
      'moduleSliderShowArrows',
      'moduleSliderLoop',
      'moduleSliderPauseOnHover',
      'moduleSliderVisibleCount',
    ]);

    let currentImage = settings.moduleImage;
    if (!currentImage) {
      if (!displayOnlyProps.has(property)) return;
      currentImage = {
        imageUrl: '',
        width: 600,
        height: 400,
        alignment: 'center',
      };
    }

    // Hizalama değişikliği kontrolü
    if (property === 'alignment' && currentImage.alignment !== value) {
      const layout = currentImage.productLayout;
      if (layout && layout.columns > 1) {
        const canDistributeEvenly = layout.columns % 2 === 0;
        const newAlignment = value as 'left' | 'center' | 'right';
        
        if (!canDistributeEvenly && (newAlignment === 'left' || newAlignment === 'right')) {
          // Eşit dağıtılamayan durumda uyarı göster
          const leftColumns = Math.ceil(layout.columns / 2);
          const rightColumns = Math.floor(layout.columns / 2);
          
          if (newAlignment === 'left') {
            setWarningMessage(`Resim sol tarafa yerleştirildiğinde ürünler sağ tarafa ${layout.columns} sütun halinde yerleştirilecek.`);
          } else {
            setWarningMessage(`Resim sağ tarafa yerleştirildiğinde ürünler sol tarafa ${layout.columns} sütun halinde yerleştirilecek.`);
          }
          
          setPendingAlignment(newAlignment);
          setWarningModalOpen(true);
          return;
        } else if (newAlignment === 'center' && !canDistributeEvenly) {
          // Orta hizalama ama eşit dağıtılamayan sütun sayısı
          const leftColumns = Math.ceil(layout.columns / 2);
          const rightColumns = Math.floor(layout.columns / 2);
          
          setWarningMessage(`Resim ortada iken ${layout.columns} sütun eşit dağıtılamaz. Sol tarafta ${leftColumns}, sağ tarafta ${rightColumns} sütun olacak.`);
          setPendingAlignment(newAlignment);
          setWarningModalOpen(true);
          return;
        }
      }
    }

    const updatedImage = {
      ...currentImage,
      [property]: value
    };

    handleSettingChange('moduleImage', updatedImage);
  };

  // Uyarı modalı onay fonksiyonu
  const handleWarningConfirm = () => {
    if (pendingAlignment) {
      const currentImage = settings.moduleImage;
      if (currentImage) {
        const updatedImage = {
          ...currentImage,
          alignment: pendingAlignment
        };
        handleSettingChange('moduleImage', updatedImage);
      }
    }
    setWarningModalOpen(false);
    setPendingAlignment(null);
    setWarningMessage('');
  };

  // Uyarı modalı iptal fonksiyonu
  const handleWarningCancel = () => {
    setWarningModalOpen(false);
    setPendingAlignment(null);
    setWarningMessage('');
  };

  // Ürün seçim fonksiyonları
  const handleProductSelection = (products: any[]) => {
    console.log('🎯 handleProductSelection çağrıldı:', products);
    const productIds = products.map(p => p.id);
    console.log('📋 Product IDs:', productIds);
    
    handleSettingChange('selectedProductIds', productIds);
    setSelectedProducts(products);
    setProductModalOpen(false);
    
    console.log('✅ Selected products updated:', products);
  };

  const handleRemoveProduct = (productId: string) => {
    const newProductIds = settings.selectedProductIds.filter(id => id !== productId);
    const newProducts = selectedProducts.filter(p => p.id !== productId);
    handleSettingChange('selectedProductIds', newProductIds);
    setSelectedProducts(newProducts);
  };

  const hasAnyModuleImage = MODULE_IMAGE_URL_KEYS.some((key) => Boolean((settings.moduleImage as any)?.[key]));
  const isProductCardEnabled = settings.moduleImage?.productCardStyle?.cardEnabled !== false;

  const applyBasicGrayModuleDisplay = () => {
    const current = settings.moduleImage || {
      imageUrl: '',
      width: 600,
      height: 400,
      alignment: 'center' as const,
    };
    handleSettingChange('moduleImage', {
      ...current,
      moduleDisplayMode: 'grid',
      pageSidePadding: 0,
      imageGap: { horizontal: 2, vertical: 0 },
      imageSeparatorV: { style: 'solid' as const, color: '#e0e0e0', width: 1 },
      imageSeparatorH: { style: 'solid' as const, color: '#e0e0e0', width: 1 },
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SectionTitle variant="h6" sx={{ mb: 0, mt: 0 }}>
              {sectionTitle} {tr('Modül Ayarları', 'Module Settings')}
            </SectionTitle>
            {onEditModule && (
              <Tooltip title={tr('Modül Adını Düzenle', 'Edit Module Name')} arrow>
                <IconButton 
                  onClick={onEditModule}
                  size="small"
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>

        <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    label={tr('Görüntüleme Sırası', 'Display Order')}
                    type="number"
                    value={(order ?? 0) + 1}
                    size="small"
                    sx={{ width: '170px' }}
                    InputProps={{ readOnly: true }}
                    helperText={tr(
                      'Sekmeleri sürükleyerek sırayı değiştirin',
                      'Drag tabs to change the order'
                    )}
                    variant="outlined"
                />
                <Tooltip title={tr('Bu Modülü Sil', 'Delete This Module')} arrow>
                    <IconButton 
                        onClick={onDelete}
                        sx={{ color: 'error.main' }}
                    >
                        <DeleteForeverIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled}
                onChange={(e) => handleSettingChange('enabled', e.target.checked)}
              />
            }
            label={tr('Bu Modülü Ana Sayfada Göster', 'Show This Module on Homepage')}
          />
        </Grid>

        {/* Yatay Konum Ayarı — marka akışı her zaman tam sayfa */}
        {!isBrandFlow && (
        <Grid item xs={12}>
          <Box sx={{ mt: 2, opacity: settings.enabled ? 1 : 0.5 }}>
            <Typography variant="body2" gutterBottom fontWeight="bold">
              {tr('Modül Yatay Konum', 'Module Horizontal Position')}: {settings.horizontalPosition ?? 50}%
            </Typography>
            <Slider
              value={settings.horizontalPosition ?? 50}
              onChange={(_, newValue) => handleSettingChange('horizontalPosition', newValue as number)}
              aria-labelledby="horizontal-position-slider"
              valueLabelDisplay="auto"
              step={5}
              min={0}
              max={100}
              disabled={!settings.enabled}
              marks={[
                { value: 0, label: tr('Sol', 'Left') },
                { value: 50, label: tr('Orta', 'Center') },
                { value: 100, label: tr('Sağ', 'Right') }
              ]}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {tr(
                'Ana sayfada modülün yatay konumunu ayarlar (0% = Sol, 50% = Orta, 100% = Sağ)',
                'Sets module horizontal position on homepage (0% = Left, 50% = Center, 100% = Right)'
              )}
            </Typography>
          </Box>
        </Grid>
        )}

        {/* Üst Boşluk Ayarı */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, opacity: settings.enabled ? 1 : 0.5 }}>
            <Typography variant="body2" gutterBottom fontWeight="bold">
              {tr('Modül Üst Boşluk', 'Module Top Spacing')}: {topSpacingDraft}px
            </Typography>
            <Slider
              value={topSpacingDraft}
              onChange={(_, newValue) => setTopSpacingDraft(newValue as number)}
              onChangeCommitted={(_, newValue) => handleSettingChange('topSpacing', newValue as number)}
              aria-labelledby="top-spacing-slider"
              valueLabelDisplay="auto"
              step={5}
              min={0}
              max={200}
              disabled={!settings.enabled}
              marks={[
                { value: 0, label: '0px' },
                { value: 100, label: '100px' },
                { value: 150, label: '150px' },
                { value: 200, label: '200px' }
              ]}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              {tr(
                'Ana sayfada modülün üstüne iç boşluk ekler, böylece modülün yüksekliğini artırır (px).',
                'Adds inner top padding on the homepage, increasing the module height (px).'
              )}
            </Typography>
          </Box>
        </Grid>

        {/* Web ve Mobil Görünüm Stili Grid'leri kaldırıldı */}
      </Grid>
      
      <Divider sx={{ my: 4 }} />

      {isBrandFlow ? (
        <BrandFlowModuleEditor
          enabled={settings.enabled}
          companyId={companyId}
          sectionTitle={sectionTitle}
          titleStyles={settings.titleStyles}
          brandFlow={settings.brandFlow}
          onSectionTitleChange={(title) => handleModuleChange('sectionTitle', title)}
          onTitleStylesChange={(styles) => handleSettingChange('titleStyles', styles)}
          onBrandFlowChange={(next) => handleSettingChange('brandFlow', next)}
        />
      ) : (
      <>
      <SectionTitle variant="h6">
        {tr('Bölüm Başlık Stilleri', 'Section Title Styles')} ({sectionTitle})
      </SectionTitle>
      
      <Grid container spacing={1} alignItems="center" sx={{ opacity: settings.enabled ? 1 : 0.5 }}>
        <Grid item xs={12} sm={4} md={2.5}>
           <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{tr('Yazı Tipi', 'Font Family')}</InputLabel>
            <Select
              value={settings.titleStyles?.fontFamily || 'Inter'}
              onChange={(e) => handleTitleStyleChange('fontFamily', e.target.value as string)}
              label={tr('Yazı Tipi', 'Font Family')}
              disabled={!settings.enabled}
            >
              {fontOptions.map(font => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>{font}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={4} sm={2} md={1.5}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{tr('Boyut', 'Size')}</InputLabel>
            <Select
              value={settings.titleStyles?.fontSize || 16}
              onChange={(e) => handleTitleStyleChange('fontSize', e.target.value as number)}
              label={tr('Boyut', 'Size')}
              disabled={!settings.enabled}
            >
              {[12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map(size => (
                <MenuItem key={size} value={size}>{size} px</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={8} sm={3} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Input
              type="color"
              value={settings.titleStyles?.color || '#111827'}
              onChange={(e) => handleTitleStyleChange('color', e.target.value)}
              disabled={!settings.enabled}
              sx={{
                padding: 0,
                border: 'none',
                width: '32px',
                height: '32px',
                minWidth: '32px',
                cursor: 'pointer',
                background: 'transparent',
                '&::-webkit-color-swatch-wrapper': { padding: 0 },
                '&::-webkit-color-swatch': { border: '1px solid #ccc', borderRadius: '4px' }
              }}
            />
            <Input
              value={settings.titleStyles?.color || '#111827'}
              onChange={(e) => handleTitleStyleChange('color', e.target.value)}
              size="small"
              sx={{ width: '70px', fontSize: '12px' }}
              disabled={!settings.enabled}
            />
          </Box>
        </Grid>

        <Grid item xs={4} sm={1.5} md={1.5}>
          <ToggleButtonGroup 
            size="small"
            value={(() => {
              const styles = [];
              if (settings.titleStyles?.bold) styles.push('bold');
              if (settings.titleStyles?.italic) styles.push('italic');
              return styles;
            })()}
            onChange={(_, newFormats) => {
              handleTitleStyleChange('bold', newFormats.includes('bold'));
              handleTitleStyleChange('italic', newFormats.includes('italic'));
            }}
            disabled={!settings.enabled}
          >
            <ToggleButton value="bold" aria-label="bold" sx={{ minWidth: '32px', px: 1 }}>
              <FormatBold fontSize="small" />
            </ToggleButton>
            <ToggleButton value="italic" aria-label="italic" sx={{ minWidth: '32px', px: 1 }}>
              <FormatItalic fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={8} sm={1.5} md={2.5} sx={{ display: 'flex', gap: 0.5 }}>
          <ToggleButtonGroup 
            size="small"
            value={settings.titleStyles?.textAlign || 'center'}
            exclusive
            onChange={(_, newAlignment) => {
              if (newAlignment !== null) {
                handleTitleStyleChange('textAlign', newAlignment);
              }
            }}
            disabled={!settings.enabled}
          >
            <ToggleButton value="left" aria-label="left align" sx={{ minWidth: '28px', px: 0.5 }}>
              <FormatAlignLeft fontSize="small" />
            </ToggleButton>
            <ToggleButton value="center" aria-label="center align" sx={{ minWidth: '28px', px: 0.5 }}>
              <FormatAlignCenter fontSize="small" />
            </ToggleButton>
            <ToggleButton value="right" aria-label="right align" sx={{ minWidth: '28px', px: 0.5 }}>
              <FormatAlignRight fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Button
            onClick={resetTitleStyles}
            startIcon={<RestartAlt fontSize="small" />}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={!settings.enabled}
            sx={{ minWidth: '60px', px: 1, fontSize: '11px' }}
          >
            {tr('Sıfırla', 'Reset')}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <SectionTitle variant="h6">
        {tr('Yazı İçeriği (HTML Editör)', 'Text Content (HTML Editor)')}
      </SectionTitle>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {tr(
          'Ürün ve Çözümler sayfasındaki gibi metin ekleyebilir, sütunlara ayırabilirsiniz. Araç çubuğundaki “Sütun” menüsünü kullanın.',
          'Add text like on Products & Solutions, and split into columns via the “Column” toolbar menu.'
        )}
      </Typography>
      <Box sx={{ opacity: settings.enabled ? 1 : 0.5, pointerEvents: settings.enabled ? 'auto' : 'none', mb: 2 }}>
        <HTMLEditor
          value={settings.htmlContent || ''}
          onChange={(html: string) => handleSettingChange('htmlContent', html)}
          placeholder={tr(
            'Bu modül için yazı içeriğini buraya yazın. HTML ve sütun düzeni desteklenir...',
            'Write module text content here. HTML and column layouts are supported...'
          )}
          disabled={!settings.enabled}
          companyId={companyId}
        />
      </Box>
      
      <Divider sx={{ my: 4 }} />

      <SectionTitle variant="h6">
        {tr('Modül Görseli', 'Module Image')}
      </SectionTitle>

      {/* Resim Yükleme Alanı - Dört Resim */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, opacity: settings.enabled ? 1 : 0.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          {tr('Görsel Yükleme (3 slot ile başlar, maksimum 16)', 'Image Upload (Starts with 3 slots, maximum 16)')}
        </Typography>
        
        <Grid container spacing={2}>
          {/* İlk Resim */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {tr('1. Resim', 'Image 1')}
            </Typography>
            
            {!settings.moduleImage?.imageUrl ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tr('1. Resim Seçin', 'Select Image 1')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {tr('Resim Kütüphanesinden', 'From Image Library')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(1)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  {tr('Resim Seç', 'Select Image')}
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Mevcut Resim */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl}
                    alt="Module image 1"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                {/* Mevcut resim için butonlar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(1)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    {tr('Değiştir', 'Change')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(1)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    {tr('Sil', 'Delete')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Başlık ve Link Formları */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                {tr('1. Resim Ayarları', 'Image 1 Settings')}
              </Typography>
              
              {/* Başlık */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Başlığı', 'Image Title')}
                placeholder={tr('Resim başlığı girin...', 'Enter image title...')}
                value={settings.moduleImage?.imageTitles?.title1 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title1: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />

              {/* Link */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Linki', 'Image Link')}
                placeholder="https://example.com or /page"
                value={settings.moduleImage?.imageLinks?.link1 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link1: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText={tr('Bu resme tıklandığında açılacak link', 'Link to open when this image is clicked')}
              />
            </Box>
          </Grid>

          {/* İkinci Resim */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {tr('2. Resim (Opsiyonel)', 'Image 2 (Optional)')}
            </Typography>
            
            {!settings.moduleImage?.imageUrl2 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tr('2. Resim Seçin', 'Select Image 2')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {tr('Resim Kütüphanesinden', 'From Image Library')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(2)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  {tr('Resim Seç', 'Select Image')}
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Mevcut Resim */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl2}
                    alt="Module image 2"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                {/* Mevcut resim için butonlar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(2)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    {tr('Değiştir', 'Change')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(2)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    {tr('Sil', 'Delete')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Başlık ve Link Formları */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                {tr('2. Resim Ayarları', 'Image 2 Settings')}
              </Typography>
              
              {/* Başlık */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Başlığı', 'Image Title')}
                placeholder={tr('Resim başlığı girin...', 'Enter image title...')}
                value={settings.moduleImage?.imageTitles?.title2 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title2: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />

              {/* Link */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Linki', 'Image Link')}
                placeholder="https://example.com or /page"
                value={settings.moduleImage?.imageLinks?.link2 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link2: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText={tr('Bu resme tıklandığında açılacak link', 'Link to open when this image is clicked')}
              />
            </Box>
          </Grid>

          {/* Üçüncü Resim */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {tr('3. Resim (Opsiyonel)', 'Image 3 (Optional)')}
            </Typography>
            
            {!settings.moduleImage?.imageUrl3 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tr('3. Resim Seçin', 'Select Image 3')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {tr('Resim Kütüphanesinden', 'From Image Library')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(3)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  {tr('Resim Seç', 'Select Image')}
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Mevcut Resim */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl3}
                    alt="Module image 3"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                {/* Mevcut resim için butonlar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(3)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    {tr('Değiştir', 'Change')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(3)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    {tr('Sil', 'Delete')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Başlık ve Link Formları */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                {tr('3. Resim Ayarları', 'Image 3 Settings')}
              </Typography>
              
              {/* Başlık */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Başlığı', 'Image Title')}
                placeholder={tr('Resim başlığı girin...', 'Enter image title...')}
                value={settings.moduleImage?.imageTitles?.title3 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title3: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />

              {/* Link */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Linki', 'Image Link')}
                placeholder="https://example.com or /page"
                value={settings.moduleImage?.imageLinks?.link3 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link3: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText={tr('Bu resme tıklandığında açılacak link', 'Link to open when this image is clicked')}
              />
            </Box>
          </Grid>

          {/* Dördüncü Resim */}
          <Grid item xs={12} sm={6} md={2} sx={{ display: visibleImageSlotCount >= 4 ? 'block' : 'none' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {tr('4. Resim (Opsiyonel)', 'Image 4 (Optional)')}
            </Typography>
            
            {!settings.moduleImage?.imageUrl4 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tr('4. Resim Seçin', 'Select Image 4')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {tr('Resim Kütüphanesinden', 'From Image Library')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(4)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  {tr('Resim Seç', 'Select Image')}
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Mevcut Resim */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl4}
                    alt="Module image 4"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                {/* Mevcut resim için butonlar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(4)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    {tr('Değiştir', 'Change')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(4)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    {tr('Sil', 'Delete')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Başlık ve Link Formları */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                {tr('4. Resim Ayarları', 'Image 4 Settings')}
              </Typography>
              
              {/* Başlık */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Başlığı', 'Image Title')}
                placeholder={tr('Resim başlığı girin...', 'Enter image title...')}
                value={settings.moduleImage?.imageTitles?.title4 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title4: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />

              {/* Link */}
              <TextField
                fullWidth
                size="small"
                label={tr('Resim Linki', 'Image Link')}
                placeholder="https://example.com or /page"
                value={settings.moduleImage?.imageLinks?.link4 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link4: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText={tr('Bu resme tıklandığında açılacak link', 'Link to open when this image is clicked')}
              />
            </Box>
          </Grid>

          {/* Beşinci Resim */}
          <Grid item xs={12} sm={6} md={2} sx={{ display: visibleImageSlotCount >= 5 ? 'block' : 'none' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              5. Resim (Opsiyonel)
            </Typography>
            
            {!settings.moduleImage?.imageUrl5 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  5. Resim Seçin
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Resim Kütüphanesinden
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(5)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Resim Seç
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Mevcut Resim */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl5}
                    alt="5. Modül resmi"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                {/* Mevcut resim için butonlar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(5)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    Değiştir
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(5)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    Sil
                  </Button>
                </Box>
              </Box>
            )}

            {/* Başlık ve Link Formları */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                5. Resim Ayarları
              </Typography>
              
              {/* Başlık */}
              <TextField
                fullWidth
                size="small"
                label="Image Title"
                placeholder="Enter image title..."
                value={settings.moduleImage?.imageTitles?.title5 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title5: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />

              {/* Link */}
              <TextField
                fullWidth
                size="small"
                label="Resim Linki"
                placeholder="https://example.com veya /sayfa"
                value={settings.moduleImage?.imageLinks?.link5 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link5: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText="Bu resme tıklandığında gidilecek link"
              />
            </Box>
          </Grid>

          {/* Altıncı Resim */}
          <Grid item xs={12} sm={6} md={2} sx={{ display: visibleImageSlotCount >= 6 ? 'block' : 'none' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              6. Resim (Opsiyonel)
            </Typography>
            
            {!settings.moduleImage?.imageUrl6 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  6. Resim Seçin
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Resim Kütüphanesinden
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(6)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Resim Seç
                </Button>
              </Box>
            ) : (
              <Box>
                {/* Mevcut Resim */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl6}
                    alt="6. Modül resmi"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                {/* Mevcut resim için butonlar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(6)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    Değiştir
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(6)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    Sil
                  </Button>
                </Box>
              </Box>
            )}

            {/* Başlık ve Link Formları */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                6. Resim Ayarları
              </Typography>
              
              {/* Başlık */}
              <TextField
                fullWidth
                size="small"
                label="Image Title"
                placeholder="Enter image title..."
                value={settings.moduleImage?.imageTitles?.title6 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title6: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />

              {/* Link */}
              <TextField
                fullWidth
                size="small"
                label="Resim Linki"
                placeholder="https://example.com veya /sayfa"
                value={settings.moduleImage?.imageLinks?.link6 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link6: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText="Bu resme tıklandığında gidilecek link"
              />
            </Box>
          </Grid>

          {/* Yedinci Resim */}
          <Grid item xs={12} sm={6} md={2} sx={{ display: visibleImageSlotCount >= 7 ? 'block' : 'none' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              7. Resim (Opsiyonel)
            </Typography>

            {!settings.moduleImage?.imageUrl7 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  7. Resim Seçin
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Resim Kütüphanesinden
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(7)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Resim Seç
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl7}
                    alt="7. Modül resmi"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(7)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    Değiştir
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(7)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    Sil
                  </Button>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                7. Resim Ayarları
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Image Title"
                placeholder="Enter image title..."
                value={settings.moduleImage?.imageTitles?.title7 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title7: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Resim Linki"
                placeholder="https://example.com veya /sayfa"
                value={settings.moduleImage?.imageLinks?.link7 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link7: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText="Bu resme tıklandığında gidilecek link"
              />
            </Box>
          </Grid>

          {/* Sekizinci Resim */}
          <Grid item xs={12} sm={6} md={2} sx={{ display: visibleImageSlotCount >= 8 ? 'block' : 'none' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              8. Resim (Opsiyonel)
            </Typography>

            {!settings.moduleImage?.imageUrl8 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  8. Resim Seçin
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Resim Kütüphanesinden
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenImageLibrary(8)}
                  startIcon={<ImageIcon />}
                  disabled={!settings.enabled}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Resim Seç
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.moduleImage?.imageUrl8}
                    alt="8. Modül resmi"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenImageLibrary(8)}
                    startIcon={<ImageIcon />}
                    disabled={!settings.enabled}
                    fullWidth
                    size="small"
                  >
                    Değiştir
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveImage(8)}
                    startIcon={<Delete />}
                    disabled={!settings.enabled}
                    size="small"
                  >
                    Sil
                  </Button>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" gutterBottom>
                8. Resim Ayarları
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Image Title"
                placeholder="Enter image title..."
                value={settings.moduleImage?.imageTitles?.title8 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const
                  };
                  const currentTitles = currentImage.imageTitles || {};
                  handleImageSettingChange('imageTitles', {
                    ...currentTitles,
                    title8: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Resim Linki"
                placeholder="https://example.com veya /sayfa"
                value={settings.moduleImage?.imageLinks?.link8 || ''}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const
                  };
                  const currentLinks = currentImage.imageLinks || {};
                  handleImageSettingChange('imageLinks', {
                    ...currentLinks,
                    link8: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                helperText="Bu resme tıklandığında gidilecek link"
              />
            </Box>
          </Grid>
          {MODULE_IMAGE_SLOTS.filter((n) => n >= 9 && n <= visibleImageSlotCount).map((imageNumber) => {
            const imageKey = MODULE_IMAGE_URL_KEYS[imageNumber - 1];
            const imageUrl = (settings.moduleImage as any)?.[imageKey] as string | undefined;
            const titleKey = `title${imageNumber}`;
            const linkKey = `link${imageNumber}`;
            return (
              <Grid item xs={12} sm={6} md={2} key={`module-image-slot-${imageNumber}`}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {imageNumber}. Resim (Opsiyonel)
                </Typography>

                {!imageUrl ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <ImageIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {imageNumber}. Resim Seçin
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Resim Kütüphanesinden
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenImageLibrary(imageNumber)}
                      startIcon={<ImageIcon />}
                      disabled={!settings.enabled}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Resim Seç
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <img
                        src={imageUrl}
                        alt={`${imageNumber}. Modül resmi`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '150px',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => handleOpenImageLibrary(imageNumber)}
                        startIcon={<ImageIcon />}
                        disabled={!settings.enabled}
                        fullWidth
                        size="small"
                      >
                        Değiştir
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveImage(imageNumber)}
                        startIcon={<Delete />}
                        disabled={!settings.enabled}
                        size="small"
                      >
                        Sil
                      </Button>
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="bold" gutterBottom>
                    {imageNumber}. Resim Ayarları
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Image Title"
                    placeholder="Enter image title..."
                    value={((settings.moduleImage as any)?.imageTitles || {})[titleKey] || ''}
                    onChange={(e) => {
                      const currentImage = settings.moduleImage || {
                        imageUrl: '',
                        width: 600,
                        height: 400,
                        alignment: 'center' as const
                      };
                      const currentTitles = (currentImage as any).imageTitles || {};
                      handleImageSettingChange('imageTitles', {
                        ...currentTitles,
                        [titleKey]: e.target.value
                      });
                    }}
                    disabled={!settings.enabled}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Resim Linki"
                    placeholder="https://example.com veya /sayfa"
                    value={((settings.moduleImage as any)?.imageLinks || {})[linkKey] || ''}
                    onChange={(e) => {
                      const currentImage = settings.moduleImage || {
                        imageUrl: '',
                        width: 600,
                        height: 400,
                        alignment: 'center' as const
                      };
                      const currentLinks = (currentImage as any).imageLinks || {};
                      handleImageSettingChange('imageLinks', {
                        ...currentLinks,
                        [linkKey]: e.target.value
                      });
                    }}
                    disabled={!settings.enabled}
                    helperText="Bu resme tıklandığında gidilecek link"
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {visibleImageSlotCount < MAX_MODULE_IMAGE_SLOTS && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setVisibleImageSlotCount((prev) => Math.min(MAX_MODULE_IMAGE_SLOTS, prev + 1))}
              disabled={!settings.enabled}
            >
              + Daha fazla ekle ({visibleImageSlotCount}/{MAX_MODULE_IMAGE_SLOTS})
            </Button>
          </Box>
        )}
      </Paper>

      {/* Resim Ayarları */}
      <Paper variant="outlined" sx={{ p: 2, opacity: hasAnyModuleImage ? 1 : 0.3 }}>


        <Typography variant="subtitle2" gutterBottom>
          Resim Ayarları
        </Typography>

        <Grid container spacing={2}>
          {/* Hizalama */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              Hizalama
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={settings.moduleImage?.alignment || 'center'}
              exclusive
              onChange={(_, newAlignment) => {
                if (newAlignment !== null) {
                  handleImageSettingChange('alignment', newAlignment);
                }
              }}
              disabled={!settings.enabled || !hasAnyModuleImage}
              fullWidth
            >
              <ToggleButton value="left" aria-label="left align">
                <FormatAlignLeft fontSize="small" />
                <Typography variant="caption" sx={{ ml: 1 }}>Sol</Typography>
              </ToggleButton>
              <ToggleButton value="center" aria-label="center align">
                <FormatAlignCenter fontSize="small" />
                <Typography variant="caption" sx={{ ml: 1 }}>Orta</Typography>
              </ToggleButton>
              <ToggleButton value="right" aria-label="right align">
                <FormatAlignRight fontSize="small" />
                <Typography variant="caption" sx={{ ml: 1 }}>{tr('Sağ', 'Right')}</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Genişlik */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              Genişlik: {settings.moduleImage?.width || 600}px
            </Typography>
            <Slider
              value={settings.moduleImage?.width || 600}
              onChange={(_, newValue) => handleImageSettingChange('width', newValue as number)}
              aria-labelledby="image-width-slider"
              valueLabelDisplay="auto"
              step={10}
              min={50}
              max={2000}
              disabled={!settings.enabled || !hasAnyModuleImage}
            />
          </Grid>

          {/* Yükseklik */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              Yükseklik: {settings.moduleImage?.height || 400}px
            </Typography>
            <Slider
              value={settings.moduleImage?.height || 400}
              onChange={(_, newValue) => handleImageSettingChange('height', newValue as number)}
              aria-labelledby="image-height-slider"
              valueLabelDisplay="auto"
              step={10}
              min={50}
              max={1200}
              disabled={!settings.enabled || !hasAnyModuleImage}
            />
          </Grid>

          {/* Sayfada Görünen Ölçek */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              {tr('Sayfada Gösterim Ölçeği', 'Display Scale on Page')}: {settings.moduleImage?.renderScale ?? 100}%
            </Typography>
            <Slider
              value={settings.moduleImage?.renderScale ?? 100}
              onChange={(_, newValue) => handleImageSettingChange('renderScale', newValue as number)}
              aria-labelledby="image-render-scale-slider"
              valueLabelDisplay="auto"
              step={5}
              min={20}
              max={100}
              disabled={!settings.enabled || !hasAnyModuleImage}
            />
          </Grid>

          {/* Mobilde Görünen Ölçek */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              {tr('Mobil Gösterim Ölçeği', 'Mobile Display Scale')}: {settings.moduleImage?.mobileRenderScale ?? 100}%
            </Typography>
            <Slider
              value={settings.moduleImage?.mobileRenderScale ?? 100}
              onChange={(_, newValue) => handleImageSettingChange('mobileRenderScale', newValue as number)}
              aria-labelledby="image-mobile-render-scale-slider"
              valueLabelDisplay="auto"
              step={5}
              min={20}
              max={100}
              disabled={!settings.enabled || !hasAnyModuleImage}
            />
          </Grid>
        </Grid>

        {/* Resimler Arası Boşluk ve Çizgi Ayarları */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Resimler Arası Boşluk & Ayırıcı
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" gutterBottom>
              Yatay Boşluk: {settings.moduleImage?.imageGap?.horizontal ?? 2}px
            </Typography>
            <Slider
              value={settings.moduleImage?.imageGap?.horizontal ?? 2}
              onChange={(_, v) => {
                const current = settings.moduleImage?.imageGap || { horizontal: 2, vertical: 0 };
                handleImageSettingChange('imageGap', { ...current, horizontal: v as number });
              }}
              valueLabelDisplay="auto"
              step={1}
              min={0}
              max={40}
              disabled={!settings.enabled}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" gutterBottom>
              Dikey Boşluk: {settings.moduleImage?.imageGap?.vertical ?? 0}px
            </Typography>
            <Slider
              value={settings.moduleImage?.imageGap?.vertical ?? 0}
              onChange={(_, v) => {
                const current = settings.moduleImage?.imageGap || { horizontal: 2, vertical: 0 };
                handleImageSettingChange('imageGap', { ...current, vertical: v as number });
              }}
              valueLabelDisplay="auto"
              step={1}
              min={0}
              max={40}
              disabled={!settings.enabled}
            />
          </Grid>
          {/* Dikey Ayırıcı (sütunlar arası) */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" gutterBottom>
              Dikey Ayırıcı (Sütunlar Arası)
            </Typography>
            <Select
              size="small"
              fullWidth
              value={settings.moduleImage?.imageSeparatorV?.style || 'none'}
              onChange={(e) => {
                const current = settings.moduleImage?.imageSeparatorV || { style: 'none' as const, color: '#e0e0e0', width: 1 };
                handleImageSettingChange('imageSeparatorV', { ...current, style: e.target.value });
              }}
              disabled={!settings.enabled}
            >
              <MenuItem value="none">Yok</MenuItem>
              <MenuItem value="solid">Solid Line</MenuItem>
              <MenuItem value="dashed">Kesikli</MenuItem>
              <MenuItem value="dotted">Dotted</MenuItem>
            </Select>
          </Grid>
          {settings.moduleImage?.imageSeparatorV?.style && settings.moduleImage.imageSeparatorV.style !== 'none' && (
            <>
              <Grid item xs={6} sm={1.5}>
                <Typography variant="body2" gutterBottom>
                  Renk
                </Typography>
                <Box
                  component="input"
                  type="color"
                  value={settings.moduleImage?.imageSeparatorV?.color || '#e0e0e0'}
                  onChange={(e: any) => {
                    const current = settings.moduleImage?.imageSeparatorV || { style: 'solid' as const, color: '#e0e0e0', width: 1 };
                    handleImageSettingChange('imageSeparatorV', { ...current, color: e.target.value });
                  }}
                  sx={{ border: 'none', width: '32px', height: '32px', p: 0, cursor: 'pointer' }}
                />
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <Typography variant="body2" gutterBottom>
                  Kalınlık: {settings.moduleImage?.imageSeparatorV?.width ?? 1}px
                </Typography>
                <Slider
                  value={settings.moduleImage?.imageSeparatorV?.width ?? 1}
                  onChange={(_, v) => {
                    const current = settings.moduleImage?.imageSeparatorV || { style: 'solid' as const, color: '#e0e0e0', width: 1 };
                    handleImageSettingChange('imageSeparatorV', { ...current, width: v as number });
                  }}
                  valueLabelDisplay="auto"
                  step={1}
                  min={1}
                  max={5}
                  disabled={!settings.enabled}
                />
              </Grid>
            </>
          )}

          {/* Yatay Ayırıcı (satırlar arası) */}
          <Grid item xs={12} sm={3}>
            <Typography variant="body2" gutterBottom>
              Yatay Ayırıcı (Satırlar Arası)
            </Typography>
            <Select
              size="small"
              fullWidth
              value={settings.moduleImage?.imageSeparatorH?.style || 'none'}
              onChange={(e) => {
                const current = settings.moduleImage?.imageSeparatorH || { style: 'none' as const, color: '#e0e0e0', width: 1 };
                handleImageSettingChange('imageSeparatorH', { ...current, style: e.target.value });
              }}
              disabled={!settings.enabled}
            >
              <MenuItem value="none">Yok</MenuItem>
              <MenuItem value="solid">Solid Line</MenuItem>
              <MenuItem value="dashed">Kesikli</MenuItem>
              <MenuItem value="dotted">Dotted</MenuItem>
            </Select>
          </Grid>
          {settings.moduleImage?.imageSeparatorH?.style && settings.moduleImage.imageSeparatorH.style !== 'none' && (
            <>
              <Grid item xs={6} sm={1.5}>
                <Typography variant="body2" gutterBottom>
                  Renk
                </Typography>
                <Box
                  component="input"
                  type="color"
                  value={settings.moduleImage?.imageSeparatorH?.color || '#e0e0e0'}
                  onChange={(e: any) => {
                    const current = settings.moduleImage?.imageSeparatorH || { style: 'solid' as const, color: '#e0e0e0', width: 1 };
                    handleImageSettingChange('imageSeparatorH', { ...current, color: e.target.value });
                  }}
                  sx={{ border: 'none', width: '32px', height: '32px', p: 0, cursor: 'pointer' }}
                />
              </Grid>
              <Grid item xs={6} sm={1.5}>
                <Typography variant="body2" gutterBottom>
                  Kalınlık: {settings.moduleImage?.imageSeparatorH?.width ?? 1}px
                </Typography>
                <Slider
                  value={settings.moduleImage?.imageSeparatorH?.width ?? 1}
                  onChange={(_, v) => {
                    const current = settings.moduleImage?.imageSeparatorH || { style: 'solid' as const, color: '#e0e0e0', width: 1 };
                    handleImageSettingChange('imageSeparatorH', { ...current, width: v as number });
                  }}
                  valueLabelDisplay="auto"
                  step={1}
                  min={1}
                  max={5}
                  disabled={!settings.enabled}
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* Eski Link Sistemi - Geriye Uyumluluk */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Genel Resim Linki (Eski Sistem - Geriye Uyumluluk)
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="https://example.com veya /sayfa"
            value={settings.moduleImage?.linkUrl || ''}
            onChange={(e) => handleImageSettingChange('linkUrl', e.target.value)}
            disabled={!settings.enabled || !settings.moduleImage?.imageUrl}
            helperText="Tüm resimlere uygulanır. Yeni sistemde her resim için ayrı link kullanın."
          />
        </Box>
      </Paper>

      {/* Başlık Stil Ayarları */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2, opacity: hasAnyModuleImage ? 1 : 0.3 }}>
        <Typography variant="subtitle2" gutterBottom>
          📝 Resim Başlık Ayarları
        </Typography>

        <Grid container spacing={2}>
          {/* Başlık Pozisyonu */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>
              Başlık Pozisyonu
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={settings.moduleImage?.titleStyles?.position || 'bottom'}
              exclusive
              onChange={(_, newPosition) => {
                if (newPosition !== null) {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitleStyles = currentImage.titleStyles || {
                    fontSize: 16,
                    color: '#333333',
                    bold: false,
                    italic: false,
                    fontFamily: 'Inter',
                    position: 'bottom' as const
                  };
                  handleImageSettingChange('titleStyles', {
                    ...currentTitleStyles,
                    position: newPosition
                  });
                }
              }}
              disabled={!settings.enabled}
              fullWidth
            >
              <ToggleButton value="top" aria-label="top">
                <Typography variant="caption" sx={{ ml: 1 }}>Top</Typography>
              </ToggleButton>
              <ToggleButton value="bottom" aria-label="bottom">
                <Typography variant="caption" sx={{ ml: 1 }}>Altta</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Font Boyutu */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>
              Font Boyutu: {settings.moduleImage?.titleStyles?.fontSize || 16}px
            </Typography>
            <Slider
              value={settings.moduleImage?.titleStyles?.fontSize || 16}
              onChange={(_, newValue) => {
                const currentImage = settings.moduleImage || { 
                  imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                };
                const currentTitleStyles = currentImage.titleStyles || {
                  fontSize: 16,
                  color: '#333333',
                  bold: false,
                  italic: false,
                  fontFamily: 'Inter',
                  position: 'bottom' as const
                };
                handleImageSettingChange('titleStyles', {
                  ...currentTitleStyles,
                  fontSize: newValue as number
                });
              }}
              aria-labelledby="title-font-size-slider"
              valueLabelDisplay="auto"
              step={1}
              min={10}
              max={32}
              disabled={!settings.enabled}
            />
          </Grid>

          {/* Renk */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              Yazı Rengi
            </Typography>
            <TextField
              type="color"
              value={settings.moduleImage?.titleStyles?.color || '#333333'}
              onChange={(e) => {
                const currentImage = settings.moduleImage || { 
                  imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                };
                const currentTitleStyles = currentImage.titleStyles || {
                  fontSize: 16,
                  color: '#333333',
                  bold: false,
                  italic: false,
                  fontFamily: 'Inter',
                  position: 'bottom' as const
                };
                handleImageSettingChange('titleStyles', {
                  ...currentTitleStyles,
                  color: e.target.value
                });
              }}
              disabled={!settings.enabled}
              size="small"
              sx={{ width: '80px' }}
            />
          </Grid>

          {/* Font Ailesi */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Font Ailesi</InputLabel>
              <Select
                value={settings.moduleImage?.titleStyles?.fontFamily || 'Inter'}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || { 
                    imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                  };
                  const currentTitleStyles = currentImage.titleStyles || {
                    fontSize: 16,
                    color: '#333333',
                    bold: false,
                    italic: false,
                    fontFamily: 'Inter',
                    position: 'bottom' as const
                  };
                  handleImageSettingChange('titleStyles', {
                    ...currentTitleStyles,
                    fontFamily: e.target.value as string
                  });
                }}
                label="Font Ailesi"
                disabled={!settings.enabled}
              >
                {fontOptions.map(font => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>{font}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Kalın/İtalik */}
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              Stil
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.titleStyles?.bold || false}
                    onChange={(e) => {
                      const currentImage = settings.moduleImage || { 
                        imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                      };
                      const currentTitleStyles = currentImage.titleStyles || {
                        fontSize: 16,
                        color: '#333333',
                        bold: false,
                        italic: false,
                        fontFamily: 'Inter',
                        position: 'bottom' as const
                      };
                      handleImageSettingChange('titleStyles', {
                        ...currentTitleStyles,
                        bold: e.target.checked
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label="Bold"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.titleStyles?.italic || false}
                    onChange={(e) => {
                      const currentImage = settings.moduleImage || { 
                        imageUrl: '', width: 600, height: 400, alignment: 'center' as const 
                      };
                      const currentTitleStyles = currentImage.titleStyles || {
                        fontSize: 16,
                        color: '#333333',
                        bold: false,
                        italic: false,
                        fontFamily: 'Inter',
                        position: 'bottom' as const
                      };
                      handleImageSettingChange('titleStyles', {
                        ...currentTitleStyles,
                        italic: e.target.checked
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label="Italic"
              />
            </Box>
          </Grid>
        </Grid>

        {/* Sıfırlama Butonu */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            onClick={() => {
              handleImageSettingChange('titleStyles', {
                fontSize: 16,
                color: '#333333',
                bold: false,
                italic: false,
                fontFamily: 'Inter',
                position: 'bottom' as const
              });
            }}
            startIcon={<RestartAlt />}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={!settings.enabled}
          >
            Başlık Stillerini Sıfırla
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
          🖼️ Modül Resim Gösterimi
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" gutterBottom>
              Görünüm
            </Typography>
            <Select
              fullWidth
              size="small"
              value={settings.moduleImage?.moduleDisplayMode || 'grid'}
              onChange={(e) => handleImageSettingChange('moduleDisplayMode', e.target.value as 'grid' | 'slider')}
              disabled={!settings.enabled}
            >
              <MenuItem value="grid">Grid</MenuItem>
              <MenuItem value="slider">Slider</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography variant="body2" gutterBottom>
              Sayfa Kenar Boşluğu (Sol/Sağ): {settings.moduleImage?.pageSidePadding ?? 44}px
            </Typography>
            <Slider
              value={settings.moduleImage?.pageSidePadding ?? 44}
              onChange={(_, newValue) => handleImageSettingChange('pageSidePadding', newValue as number)}
              min={0}
              max={100}
              step={2}
              valueLabelDisplay="auto"
              disabled={!settings.enabled}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={applyBasicGrayModuleDisplay}
            disabled={!settings.enabled}
          >
            Temel Gri Uygula
          </Button>
        </Box>

        {(settings.moduleImage?.moduleDisplayMode || 'grid') === 'slider' && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Ekranda Görünen Resim (Web): {settings.moduleImage?.moduleSliderVisibleCount || 4}
              </Typography>
              <Slider
                value={settings.moduleImage?.moduleSliderVisibleCount || 4}
                onChange={(_, newValue) => handleImageSettingChange('moduleSliderVisibleCount', newValue as number)}
                min={1}
                max={8}
                step={1}
                valueLabelDisplay="auto"
                disabled={!settings.enabled}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Otomatik Kaydırma Süresi: {settings.moduleImage?.moduleSliderInterval || 4000} ms
              </Typography>
              <Slider
                value={settings.moduleImage?.moduleSliderInterval || 4000}
                onChange={(_, newValue) => handleImageSettingChange('moduleSliderInterval', newValue as number)}
                min={1500}
                max={10000}
                step={500}
                valueLabelDisplay="auto"
                disabled={!settings.enabled || !(settings.moduleImage?.moduleSliderAutoplay === true)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.moduleImage?.moduleSliderAutoplay === true}
                      onChange={(e) => handleImageSettingChange('moduleSliderAutoplay', e.target.checked)}
                      disabled={!settings.enabled}
                    />
                  }
                  label="Auto Scroll"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.moduleImage?.moduleSliderShowArrows !== false}
                      onChange={(e) => handleImageSettingChange('moduleSliderShowArrows', e.target.checked)}
                      disabled={!settings.enabled}
                    />
                  }
                  label="Show Arrows"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.moduleImage?.moduleSliderLoop !== false}
                      onChange={(e) => handleImageSettingChange('moduleSliderLoop', e.target.checked)}
                      disabled={!settings.enabled}
                    />
                  }
                  label="Infinite Loop"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.moduleImage?.moduleSliderPauseOnHover !== false}
                      onChange={(e) => handleImageSettingChange('moduleSliderPauseOnHover', e.target.checked)}
                      disabled={!settings.enabled}
                    />
                  }
                  label="Hover'da Duraklat"
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Ürün Yerleşim Ayarları */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="body2" gutterBottom fontWeight="bold">
          Ürün Yerleşim Ayarları
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* Satır Sayısı */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" gutterBottom>
              Satır Sayısı
            </Typography>
            <Select
              fullWidth
              size="small"
              value={Math.min(settings.moduleImage?.productLayout?.rows || 2, 2)}
              onChange={(e) => handleImageSettingChange('productLayout', {
                ...settings.moduleImage?.productLayout,
                rows: Number(e.target.value)
              })}
              disabled={!settings.enabled}
            >
              {[1, 2].map(num => (
                <MenuItem key={num} value={num}>{num} {tr('Satır', 'Rows')}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Sütun Sayısı */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" gutterBottom>
              Sütun Sayısı
            </Typography>
            <Select
              fullWidth
              size="small"
              value={settings.moduleImage?.productLayout?.columns || 2}
              onChange={(e) => handleImageSettingChange('productLayout', {
                ...settings.moduleImage?.productLayout,
                rows: settings.moduleImage?.productLayout?.rows || 2,
                columns: Number(e.target.value)
              })}
              disabled={!settings.enabled}
            >
              {[1, 2, 3, 4, 5, 6].map(num => (
                <MenuItem key={num} value={num}>{num} {tr('Sütun', 'Columns')}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Toplam Ürün Sayısı Göstergesi */}
        <Box sx={{ 
          p: 1, 
          backgroundColor: 'primary.main', 
          color: 'white', 
          borderRadius: 1, 
          textAlign: 'center',
          mb: 1
        }}>
          <Typography variant="caption" fontWeight="bold">
            Toplam: {Math.min(settings.moduleImage?.productLayout?.rows || 2, 2) * (settings.moduleImage?.productLayout?.columns || 2)} ürün gösterilecek
          </Typography>
        </Box>

        {/* Uyarı Metni */}
        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
          <Typography variant="caption">
            • Maksimum 2x6 = 12 ürün eklenebilir<br/>
            • Satır/sütun ayarı ürün ve kategori kartlarının grid görünümünü belirler<br/>
            • Modül resmi olmasa da ürün gridi bu ayarlara göre render edilir
          </Typography>
        </Alert>
      </Box>

      {!hasAnyModuleImage && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Resim ayarlarını kullanmak için önce en az bir resim yükleyin.
        </Alert>
      )}

      {/* Ürün Kartı Stil Ayarları */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          🎨 Ürün Kartı Stil Ayarları
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isProductCardEnabled}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '',
                    width: 600,
                    height: 400,
                    alignment: 'center' as const,
                    productLayout: { rows: 2, columns: 2 }
                  };
                  const currentCardStyle = currentImage.productCardStyle || {};
                  handleSettingChange('moduleImage', {
                    ...currentImage,
                    productCardStyle: {
                      ...currentCardStyle,
                      cardEnabled: e.target.checked
                    }
                  });
                }}
                disabled={!settings.enabled}
              />
            }
            label={tr('Ürün Kartı Aktif', 'Product Card Active')}
          />
        </Box>

        <Box sx={{ opacity: isProductCardEnabled ? 1 : 0.5, pointerEvents: isProductCardEnabled ? 'auto' : 'none' }}>

        {/* Görünürlük Ayarları */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            Görünürlük Ayarları
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.moduleImage?.productCardStyle?.showProductName !== false}
                  onChange={(e) => {
                    const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                    handleImageSettingChange('productCardStyle', {
                      ...currentCardStyle,
                      showProductName: e.target.checked
                    });
                  }}
                  disabled={!settings.enabled}
                />
              }
              label={tr('Ürün Adını Göster', 'Show Product Name')}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.moduleImage?.productCardStyle?.showPrice !== false}
                  onChange={(e) => {
                    const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                    handleImageSettingChange('productCardStyle', {
                      ...currentCardStyle,
                      showPrice: e.target.checked
                    });
                  }}
                  disabled={!settings.enabled}
                />
              }
              label={tr('Fiyatı Göster', 'Show Price')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.moduleImage?.productCardStyle?.showOldPrice !== false}
                  onChange={(e) => {
                    const currentImage = settings.moduleImage || {
                      imageUrl: '',
                      width: 600,
                      height: 400,
                      alignment: 'center' as const,
                      productLayout: { rows: 2, columns: 2 }
                    };
                    const currentCardStyle = currentImage.productCardStyle || {};
                    handleSettingChange('moduleImage', {
                      ...currentImage,
                      productCardStyle: {
                        ...currentCardStyle,
                        showOldPrice: e.target.checked
                      }
                    });
                  }}
                  disabled={!settings.enabled}
                />
              }
              label={tr('Eski Fiyatı Göster', 'Show Old Price')}
            />
          </Box>
        </Box>

        {/* Ürün Adı Stil Ayarları */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            Ürün Adı Stilleri
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Renk */}
            <Box sx={{ minWidth: '120px' }}>
              <Typography variant="body2" gutterBottom>
                Yazı Rengi
              </Typography>
              <TextField
                type="color"
                value={settings.moduleImage?.productCardStyle?.productNameColor || '#ffffff'}
                onChange={(e) => {
                  const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                  handleImageSettingChange('productCardStyle', {
                    ...currentCardStyle,
                    productNameColor: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                size="small"
                sx={{ width: '80px' }}
              />
            </Box>

            {/* Font Boyutu */}
            <Box sx={{ minWidth: '200px', flex: 1 }}>
              <Typography variant="body2" gutterBottom>
                Font Boyutu: {settings.moduleImage?.productCardStyle?.productNameFontSize || 14}px
              </Typography>
              <Slider
                value={settings.moduleImage?.productCardStyle?.productNameFontSize || 14}
                onChange={(_, newValue) => {
                  const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                  handleImageSettingChange('productCardStyle', {
                    ...currentCardStyle,
                    productNameFontSize: newValue as number
                  });
                }}
                min={10}
                max={24}
                step={1}
                disabled={!settings.enabled}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Font Ağırlığı ve Stil */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.productCardStyle?.productNameBold || false}
                    onChange={(e) => {
                      const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                      handleImageSettingChange('productCardStyle', {
                        ...currentCardStyle,
                        productNameBold: e.target.checked
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label="Bold"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.productCardStyle?.productNameItalic || false}
                    onChange={(e) => {
                      const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                      handleImageSettingChange('productCardStyle', {
                        ...currentCardStyle,
                        productNameItalic: e.target.checked
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label="Italic"
              />
            </Box>
          </Box>
        </Box>

        {/* Fiyat Stil Ayarları */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            Fiyat Stilleri
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Renk */}
            <Box sx={{ minWidth: '120px' }}>
              <Typography variant="body2" gutterBottom>
                Yazı Rengi
              </Typography>
              <TextField
                type="color"
                value={settings.moduleImage?.productCardStyle?.priceColor || '#fbbf24'}
                onChange={(e) => {
                  const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                  handleImageSettingChange('productCardStyle', {
                    ...currentCardStyle,
                    priceColor: e.target.value
                  });
                }}
                disabled={!settings.enabled}
                size="small"
                sx={{ width: '80px' }}
              />
            </Box>

            {/* Font Boyutu */}
            <Box sx={{ minWidth: '200px', flex: 1 }}>
              <Typography variant="body2" gutterBottom>
                Font Boyutu: {settings.moduleImage?.productCardStyle?.priceFontSize || 14}px
              </Typography>
              <Slider
                value={settings.moduleImage?.productCardStyle?.priceFontSize || 14}
                onChange={(_, newValue) => {
                  const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                  handleImageSettingChange('productCardStyle', {
                    ...currentCardStyle,
                    priceFontSize: newValue as number
                  });
                }}
                min={10}
                max={24}
                step={1}
                disabled={!settings.enabled}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Font Ağırlığı ve Stil */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.productCardStyle?.priceBold !== false}
                    onChange={(e) => {
                      const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                      handleImageSettingChange('productCardStyle', {
                        ...currentCardStyle,
                        priceBold: e.target.checked
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label="Bold"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.productCardStyle?.priceItalic || false}
                    onChange={(e) => {
                      const currentCardStyle = settings.moduleImage?.productCardStyle || {};
                      handleImageSettingChange('productCardStyle', {
                        ...currentCardStyle,
                        priceItalic: e.target.checked
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label="Italic"
              />
            </Box>
          </Box>
        </Box>


        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            🎨 Ürün Kartı Boyut Ayarları
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>
                Kart Genişliği: {settings.moduleImage?.productCardSize?.width || 200}px
              </Typography>
              <Slider
                value={settings.moduleImage?.productCardSize?.width || 200}
                onChange={(_, newValue) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '',
                    width: 600,
                    height: 400,
                    alignment: 'center' as const,
                    productCardSize: { width: 200, height: 250 }
                  };
                  const currentProductCardSize = currentImage.productCardSize || { width: 200, height: 250 };
                  handleImageSettingChange('productCardSize', {
                    ...currentProductCardSize,
                    width: newValue as number
                  });
                }}
                aria-labelledby="card-width-slider"
                valueLabelDisplay="auto"
                step={10}
                min={150}
                max={400}
                disabled={!settings.enabled}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>
                Kart Yüksekliği: {settings.moduleImage?.productCardSize?.height || 250}px
              </Typography>
              <Slider
                value={settings.moduleImage?.productCardSize?.height || 250}
                onChange={(_, newValue) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '',
                    width: 600,
                    height: 400,
                    alignment: 'center' as const,
                    productCardSize: { width: 200, height: 250 }
                  };
                  const currentProductCardSize = currentImage.productCardSize || { width: 200, height: 250 };
                  handleImageSettingChange('productCardSize', {
                    ...currentProductCardSize,
                    height: newValue as number
                  });
                }}
                aria-labelledby="card-height-slider"
                valueLabelDisplay="auto"
                step={10}
                min={200}
                max={500}
                disabled={!settings.enabled}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Bu ayarlar sadece seçilen ürün kartlarının boyutlarını etkiler. Modül resmi boyutlarından bağımsızdır.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
            ↔️ Ürün Yerleşim Ayarları
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Hizalama
              </Typography>
              <Select
                size="small"
                fullWidth
                value={settings.moduleImage?.productCardStyle?.productGrid?.alignment || 'center'}
                onChange={(e) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '',
                    width: 600,
                    height: 400,
                    alignment: 'center' as const,
                    productLayout: { rows: 2, columns: 2 }
                  };
                  const currentCardStyle = currentImage.productCardStyle || {};
                  const currentGrid = currentCardStyle.productGrid || { alignment: 'center', horizontalGap: 12, verticalGap: 12 };
                  handleSettingChange('moduleImage', {
                    ...currentImage,
                    productCardStyle: {
                      ...currentCardStyle,
                      productGrid: {
                        ...currentGrid,
                        alignment: e.target.value as 'left' | 'center' | 'right'
                      }
                    }
                  });
                }}
                disabled={!settings.enabled}
              >
                <MenuItem value="left">Sola Yasla</MenuItem>
                <MenuItem value="center">Ortala</MenuItem>
                <MenuItem value="right">{tr('Sağa Yasla', 'Align Right')}</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Yatay Boşluk: {settings.moduleImage?.productCardStyle?.productGrid?.horizontalGap ?? 12}px
              </Typography>
              <Slider
                value={settings.moduleImage?.productCardStyle?.productGrid?.horizontalGap ?? 12}
                onChange={(_, newValue) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '',
                    width: 600,
                    height: 400,
                    alignment: 'center' as const,
                    productLayout: { rows: 2, columns: 2 }
                  };
                  const currentCardStyle = currentImage.productCardStyle || {};
                  const currentGrid = currentCardStyle.productGrid || { alignment: 'center', horizontalGap: 12, verticalGap: 12 };
                  handleSettingChange('moduleImage', {
                    ...currentImage,
                    productCardStyle: {
                      ...currentCardStyle,
                      productGrid: {
                        ...currentGrid,
                        horizontalGap: newValue as number
                      }
                    }
                  });
                }}
                min={0}
                max={40}
                step={1}
                valueLabelDisplay="auto"
                disabled={!settings.enabled}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Dikey Boşluk: {settings.moduleImage?.productCardStyle?.productGrid?.verticalGap ?? 12}px
              </Typography>
              <Slider
                value={settings.moduleImage?.productCardStyle?.productGrid?.verticalGap ?? 12}
                onChange={(_, newValue) => {
                  const currentImage = settings.moduleImage || {
                    imageUrl: '',
                    width: 600,
                    height: 400,
                    alignment: 'center' as const,
                    productLayout: { rows: 2, columns: 2 }
                  };
                  const currentCardStyle = currentImage.productCardStyle || {};
                  const currentGrid = currentCardStyle.productGrid || { alignment: 'center', horizontalGap: 12, verticalGap: 12 };
                  handleSettingChange('moduleImage', {
                    ...currentImage,
                    productCardStyle: {
                      ...currentCardStyle,
                      productGrid: {
                        ...currentGrid,
                        verticalGap: newValue as number
                      }
                    }
                  });
                }}
                min={0}
                max={40}
                step={1}
                valueLabelDisplay="auto"
                disabled={!settings.enabled}
              />
            </Grid>
          </Grid>
        </Box>
        </Box>

        {!settings.enabled && (
          <Alert severity="warning">
            Stil ayarlarını değiştirmek için önce modülü aktif hale getirin.
          </Alert>
        )}
      </Paper>

      {/* Ürün Gösterimi Bölümü */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={12}>
          <SectionTitle variant="h6">
            📦 Ürün Gösterimi
          </SectionTitle>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                {t('ecommerceHomeSettings.productSource.title')}
              </Typography>
              <RadioGroup
                row
                value={isCategorySource ? 'category' : 'manual'}
                onChange={(e) => {
                  const nextSource = e.target.value as 'manual' | 'category';
                  if (nextSource === 'category') {
                    handleSettingsPatch(applyHomepageModuleTemplate(settings, 'category-row'));
                    return;
                  }
                  handleSettingsPatch({
                    productSource: 'manual',
                    moduleTemplate: 'default',
                  });
                }}
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  disabled={!settings.enabled}
                  label={t('ecommerceHomeSettings.productSource.manual')}
                />
                <FormControlLabel
                  value="category"
                  control={<Radio />}
                  disabled={!settings.enabled}
                  label={t('ecommerceHomeSettings.productSource.category')}
                />
              </RadioGroup>
              {isCategorySource && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('ecommerceHomeSettings.productSource.categoryHelp')}
                </Typography>
              )}
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!settings.enabled}
                  onClick={() => handleSettingsPatch(applyHomepageModuleTemplate(settings, 'category-row'))}
                >
                  {t('ecommerceHomeSettings.productSource.applyTemplate')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {isCategorySource
                  ? `${t('ecommerceHomeSettings.productSource.selectedCategories')} (${settings.selectedCategoryIds?.length || 0})`
                  : `Seçilen İçerik (${settings.selectedProductIds?.length || 0})`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isCategorySource ? (
                  <Button
                    variant="contained"
                    onClick={() => setCategoryModalOpen(true)}
                    disabled={!settings.enabled}
                    sx={{ minWidth: '120px' }}
                  >
                    {t('ecommerceHomeSettings.productSource.selectCategory')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => setProductModalOpen(true)}
                    disabled={!settings.enabled}
                    sx={{ minWidth: '120px' }}
                  >
                    Ürün Seç
                  </Button>
                )}
              </Box>
            </Box>

            {isCategorySource && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff7ed', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showViewAllButton !== false}
                      onChange={(e) => handleSettingChange('showViewAllButton', e.target.checked)}
                      disabled={!settings.enabled}
                    />
                  }
                  label={t('ecommerceHomeSettings.productSource.viewAll')}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {t('ecommerceHomeSettings.productSource.viewAllHelp')}
                </Typography>
                <TextField
                  size="small"
                  label={t('ecommerceHomeSettings.productSource.viewAllText')}
                  value={settings.viewAllButtonText || 'Tümü'}
                  onChange={(e) => handleSettingChange('viewAllButtonText', e.target.value)}
                  disabled={!settings.enabled || settings.showViewAllButton === false}
                  sx={{ maxWidth: 220 }}
                />
              </Box>
            )}

            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                ↔️ Sayfa Kenar Boşluğu
              </Typography>
              <Typography variant="body2" gutterBottom>
                Sol/Sağ: {settings.moduleImage?.pageSidePadding ?? 44}px
              </Typography>
              <Slider
                value={settings.moduleImage?.pageSidePadding ?? 44}
                onChange={(_, newValue) => handleImageSettingChange('pageSidePadding', newValue as number)}
                min={0}
                max={100}
                step={2}
                valueLabelDisplay="auto"
                disabled={!settings.enabled}
              />
            </Box>

            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                🏷️ Marka Sekmeleri
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.moduleImage?.productCardStyle?.brandTabsEnabled === true}
                    onChange={(e) => {
                      const currentImage = settings.moduleImage || {
                        imageUrl: '',
                        width: 600,
                        height: 400,
                        alignment: 'center' as const,
                        productLayout: { rows: 2, columns: 2 }
                      };
                      const currentCardStyle = currentImage.productCardStyle || {};
                      handleSettingChange('moduleImage', {
                        ...currentImage,
                        productCardStyle: {
                          ...currentCardStyle,
                          brandTabsEnabled: e.target.checked
                        }
                      });
                    }}
                    disabled={!settings.enabled}
                  />
                }
                label={tr('Marka sekmelerini etkinleştir (seçilen ürün markalarından otomatik oluşturulur)', 'Enable brand tabs (automatically generated from selected product brands)')}
              />
            </Box>

            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                🎞️ Ürün Gösterim Tipi
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" gutterBottom>
                    Görünüm
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={settings.moduleImage?.productCardStyle?.displayMode || 'grid'}
                    onChange={(e) => {
                      const currentImage = settings.moduleImage || {
                        imageUrl: '',
                        width: 600,
                        height: 400,
                        alignment: 'center' as const,
                        productLayout: { rows: 2, columns: 2 }
                      };
                      const currentCardStyle = currentImage.productCardStyle || {};
                      handleSettingChange('moduleImage', {
                        ...currentImage,
                        productCardStyle: {
                          ...currentCardStyle,
                          displayMode: e.target.value as 'grid' | 'slider'
                        }
                      });
                    }}
                    disabled={!settings.enabled}
                  >
                    <MenuItem value="grid">{tr('Izgara (Satır/Sütun)', 'Grid (Rows/Columns)')}</MenuItem>
                    <MenuItem value="slider">{tr('Kaydırıcı (Tek satır, kaydırılabilir)', 'Slider (Single row, scrollable)')}</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Box>

            {((settings.moduleImage?.productCardStyle?.displayMode || 'grid') === 'slider') && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  ⏯️ Slider Ayarları
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" gutterBottom>
                      Ekranda Görünen Kart (Web): {settings.moduleImage?.productCardStyle?.sliderVisibleCount ?? 6}
                    </Typography>
                    <Slider
                      value={settings.moduleImage?.productCardStyle?.sliderVisibleCount ?? 6}
                      onChange={(_, newValue) => {
                        const currentImage = settings.moduleImage || {
                          imageUrl: '',
                          width: 600,
                          height: 400,
                          alignment: 'center' as const,
                          productLayout: { rows: 2, columns: 2 }
                        };
                        const currentCardStyle = currentImage.productCardStyle || {};
                        handleSettingChange('moduleImage', {
                          ...currentImage,
                          productCardStyle: {
                            ...currentCardStyle,
                            sliderVisibleCount: newValue as number
                          }
                        });
                      }}
                      min={1}
                      max={10}
                      step={1}
                      valueLabelDisplay="auto"
                      disabled={!settings.enabled}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" gutterBottom>
                      Otomatik Kaydırma Süresi: {settings.moduleImage?.productCardStyle?.sliderInterval ?? 4000} ms
                    </Typography>
                    <Slider
                      value={settings.moduleImage?.productCardStyle?.sliderInterval ?? 4000}
                      onChange={(_, newValue) => {
                        const currentImage = settings.moduleImage || {
                          imageUrl: '',
                          width: 600,
                          height: 400,
                          alignment: 'center' as const,
                          productLayout: { rows: 2, columns: 2 }
                        };
                        const currentCardStyle = currentImage.productCardStyle || {};
                        handleSettingChange('moduleImage', {
                          ...currentImage,
                          productCardStyle: {
                            ...currentCardStyle,
                            sliderInterval: newValue as number
                          }
                        });
                      }}
                      min={1500}
                      max={10000}
                      step={500}
                      valueLabelDisplay="auto"
                      disabled={!settings.enabled || !(settings.moduleImage?.productCardStyle?.sliderAutoplay === true)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.moduleImage?.productCardStyle?.sliderAutoplay === true}
                            onChange={(e) => {
                              const currentImage = settings.moduleImage || {
                                imageUrl: '',
                                width: 600,
                                height: 400,
                                alignment: 'center' as const,
                                productLayout: { rows: 2, columns: 2 }
                              };
                              const currentCardStyle = currentImage.productCardStyle || {};
                              handleSettingChange('moduleImage', {
                                ...currentImage,
                                productCardStyle: {
                                  ...currentCardStyle,
                                  sliderAutoplay: e.target.checked
                                }
                              });
                            }}
                            disabled={!settings.enabled}
                          />
                        }
                        label="Auto Scroll"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.moduleImage?.productCardStyle?.sliderShowArrows !== false}
                            onChange={(e) => {
                              const currentImage = settings.moduleImage || {
                                imageUrl: '',
                                width: 600,
                                height: 400,
                                alignment: 'center' as const,
                                productLayout: { rows: 2, columns: 2 }
                              };
                              const currentCardStyle = currentImage.productCardStyle || {};
                              handleSettingChange('moduleImage', {
                                ...currentImage,
                                productCardStyle: {
                                  ...currentCardStyle,
                                  sliderShowArrows: e.target.checked
                                }
                              });
                            }}
                            disabled={!settings.enabled}
                          />
                        }
                        label="Show Arrows"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.moduleImage?.productCardStyle?.sliderLoop !== false}
                            onChange={(e) => {
                              const currentImage = settings.moduleImage || {
                                imageUrl: '',
                                width: 600,
                                height: 400,
                                alignment: 'center' as const,
                                productLayout: { rows: 2, columns: 2 }
                              };
                              const currentCardStyle = currentImage.productCardStyle || {};
                              handleSettingChange('moduleImage', {
                                ...currentImage,
                                productCardStyle: {
                                  ...currentCardStyle,
                                  sliderLoop: e.target.checked
                                }
                              });
                            }}
                            disabled={!settings.enabled}
                          />
                        }
                        label="Infinite Loop"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.moduleImage?.productCardStyle?.sliderPauseOnHover !== false}
                            onChange={(e) => {
                              const currentImage = settings.moduleImage || {
                                imageUrl: '',
                                width: 600,
                                height: 400,
                                alignment: 'center' as const,
                                productLayout: { rows: 2, columns: 2 }
                              };
                              const currentCardStyle = currentImage.productCardStyle || {};
                              handleSettingChange('moduleImage', {
                                ...currentImage,
                                productCardStyle: {
                                  ...currentCardStyle,
                                  sliderPauseOnHover: e.target.checked
                                }
                              });
                            }}
                            disabled={!settings.enabled}
                          />
                        }
                        label="Hover'da Duraklat"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Seçilen İçerik Tablosu - Ürünler ve Kategoriler */}

            {isCategorySource ? (
              (settings.selectedCategoryIds && settings.selectedCategoryIds.length > 0) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {selectedCategoryRows.map((category) => (
                    <Paper key={category.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {category.imageUrl ? (
                            <Avatar src={category.imageUrl} alt={category.name} />
                          ) : (
                            <Avatar sx={{ bgcolor: 'grey.200' }}>
                              <CategoryIcon sx={{ color: 'grey.600' }} />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {category.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('ecommerceHomeSettings.productSource.productCount', { count: category.productCount })}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton size="small" color="error" onClick={() => handleRemoveCategory(category.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('ecommerceHomeSettings.productSource.previewHint')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {category.products.length > 0 ? (
                          category.products.map((product) => (
                            <Chip key={product.id} label={product.name} size="small" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Bu kategoride henüz ürün yok.
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  {t('ecommerceHomeSettings.productSource.noCategory')}
                </Alert>
              )
            ) : (settings.selectedProductIds && settings.selectedProductIds.length > 0) ? (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '80px' }}>Resim</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell sx={{ width: '120px' }}>Type/SKU</TableCell>
                      <TableCell sx={{ width: '60px' }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Ürün Satırları */}
                    {selectedProducts.map((product) => {
                      // Ürün resimlerini işle (virgülle ayrılmış olabilir)
                      const imageUrls = product.imageUrl ? 
                        (typeof product.imageUrl === 'string' ? product.imageUrl.split(',').filter(Boolean) : [product.imageUrl])
                        : [];
                      const primaryImage = imageUrls.length > 0 ? imageUrls[0] : null;

                      return (
                        <TableRow key={`product-${product.id}`} hover>
                          {/* Ürün Resmi */}
                          <TableCell>
                            {primaryImage ? (
                              <Box
                                component="img"
                                src={primaryImage}
                                alt={product.name}
                                sx={{
                                  width: '60px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0'
                                }}
                                onError={(e) => {
                                  // Resim yüklenemezse fallback göster
                                  e.currentTarget.style.display = 'none';
                                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                  if (nextElement) {
                                    nextElement.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            
                            {/* Fallback Resim Alanı */}
                            <Box
                              sx={{
                                width: '60px',
                                height: '60px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                display: primaryImage ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <ImageIcon sx={{ color: '#bdbdbd', fontSize: '24px' }} />
                            </Box>
                          </TableCell>

                          {/* Ürün Adı */}
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium" title={product.name}>
                              {product.name}
                            </Typography>
                            {imageUrls.length > 1 && (
                              <Typography variant="caption" color="primary.main">
                                +{imageUrls.length - 1} resim daha
                              </Typography>
                            )}
                          </TableCell>

                          {/* SKU */}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              Ürün - {product.stockCode}
                            </Typography>
                          </TableCell>

                          {/* Silme Butonu */}
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveProduct(product.id)}
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                Henüz içerik seçilmedi. "Ürün Seç" butonunu kullanarak modülde gösterilecek ürünleri seçin.
              </Alert>
            )}

            {!settings.enabled && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                İçerik seçmek için önce modülü aktif hale getirin.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
      </>
      )}

      {/* Ürün Seçim Modalı */}
      <SimpleProductSelectionModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSelect={handleProductSelection}
        companyId={companyId}
        selectedProductIds={settings.selectedProductIds || []}
        maxSelection={settings.maxProducts || 12}
      />

      <CategorySelectionModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onSelect={handleCategorySelection}
        companyId={companyId}
        selectedCategoryIds={settings.selectedCategoryIds || []}
        maxSelection={settings.maxCategories || 12}
      />

      {/* Resim Kütüphanesi Modalı */}
      <ImageLibrarySelectionModal
        open={imageLibraryModalOpen}
        onClose={() => {
          setImageLibraryModalOpen(false);
          setCurrentImageSlot(null);
        }}
        onSave={handleSaveImageFromLibrary}
        companyId={companyId}
        productName={`Modül Resmi ${currentImageSlot || ''}`}
        currentImageUrls={[]}
        productId={null}
        maxSelection={16}
      />

      {/* Uyarı Modalı */}
      <Dialog
        open={warningModalOpen}
        onClose={handleWarningCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Alert severity="warning" sx={{ mr: 1, p: 0, '& .MuiAlert-icon': { m: 0 } }}>
              <span>⚠️</span>
            </Alert>
            Düzen Uyarısı
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {warningMessage}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Devam etmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleWarningCancel}
            color="inherit"
          >
            İptal
          </Button>
          <Button
            onClick={handleWarningConfirm}
            color="primary"
            variant="contained"
          >
            Devam Et
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ModuleSekmesi; 